/**
 * B2BCatalogPage — Phase 33 (Supplier Filter) / Phase 64 (Backorder Policy) / Phase 92 (Group Filters)
 *
 * Phase 92:
 *   • When a specific supplier is selected, loads that supplier's B2B filter groups
 *     via get_b2b_supplier_filter_groups() RPC.
 *   • A multi-select group filter dropdown appears only when the supplier has
 *     groups marked use_as_b2b_filter = true.
 *   • Selecting groups passes them to get_b2b_catalog() which recursively expands
 *     each group to include all descendants, then filters products.
 *   • Clearing the supplier filter also clears the group filter.
 *
 * Phase 64:
 *   • Product list fetched via get_b2b_catalog() RPC instead of plain PostgREST.
 *   • RPC returns available_qty (SUM of movements) and effective_backorder_allowed
 *     (COALESCE(product_override, company_global)).
 *   • Products with available_qty <= 0 AND effective_backorder_allowed = false
 *     are rendered with a disabled "אזל מהמלאי" button instead of Add-to-Cart.
 *
 * Phase 33:
 *   • "סינון לפי ספק" dropdown, dynamically populated from allowedCompanyIds.
 *   • Supplier filter auto-resets on branch switch.
 *
 * Phase 4.1 (original):
 *   • Grid / List view toggle. Inline QtyStepper. B2BCartContext.
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';

import DOMPurify         from 'dompurify';
import { supabase }      from '@/lib/supabase';
import { useAuth }       from '@/features/auth/AuthContext';
import { useB2BCart, type CatalogProduct } from './B2BCartContext';
import { useB2BUser } from './B2BUserContext';
import { Loader as Loader2, CircleAlert as AlertCircle, Search, LayoutGrid, List, ShoppingBag, Plus, Minus, ShoppingCart, CircleCheck as CheckCircle2, Store, ChevronDown, Building2, X as XIcon, PackageX, ListFilter as Filter, FolderTree, Eye, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatPrice(n: number) {
  return `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Inline quantity stepper ──────────────────────────────────────────────────

interface QtyStepperProps {
  value:    number;
  onChange: (n: number) => void;
  min?:     number;
}
function QtyStepper({ value, onChange, min = 1 }: QtyStepperProps) {
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200
                   text-gray-500 hover:bg-gray-100 active:scale-90 transition-all"
      >
        <Minus className="w-3 h-3" />
      </button>
      <input
        type="number"
        min={min}
        value={value}
        onChange={e => {
          const n = parseInt(e.target.value, 10);
          if (!isNaN(n) && n >= min) onChange(n);
        }}
        className="w-10 h-7 text-center text-sm font-bold border border-gray-200
                   rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300
                   focus:border-transparent"
      />
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200
                   text-gray-500 hover:bg-gray-100 active:scale-90 transition-all"
      >
        <Plus className="w-3 h-3" />
      </button>
    </div>
  );
}

// ─── Supplier badge ───────────────────────────────────────────────────────────

function SupplierBadge({ name }: { name: string | null }) {
  if (!name) return null;
  return (
    <div className="flex items-center gap-1 min-w-0">
      <Store className="w-3 h-3 shrink-0 text-gray-400" />
      <span
        className="text-[10px] text-gray-400 font-medium truncate leading-none"
        title={name}
      >
        {name}
      </span>
    </div>
  );
}

// ─── Product Detail Modal ─────────────────────────────────────────────────────

function ProductDetailModal({
  product, images, onClose, qty, onQtyChange, onAdd, vatRate, isVatLiable, viewOnly,
}: {
  product:     CatalogProduct;
  images:      { image_url: string; alt_text: string | null; position: number }[];
  onClose:     () => void;
  qty:         number;
  onQtyChange: (n: number) => void;
  onAdd:       () => void;
  vatRate:     number;
  isVatLiable: boolean;
  viewOnly?:   boolean;
}) {
  const allImages = images.length > 0
    ? images.sort((a, b) => a.position - b.position).map(i => i.image_url)
    : product.image_url ? [product.image_url] : [];
  const [idx, setIdx] = useState(0);
  const isOos = product.available_qty <= 0 && !product.effective_backorder_allowed;

  const prev = () => setIdx(i => (i <= 0 ? allImages.length - 1 : i - 1));
  const next = () => setIdx(i => (i >= allImages.length - 1 ? 0 : i + 1));

  // Swipe support
  const touchStart = useRef<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => { touchStart.current = e.touches[0]?.clientX ?? null; };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const endX = e.changedTouches[0]?.clientX ?? touchStart.current;
    const diff = touchStart.current - endX;
    if (Math.abs(diff) > 50) { diff > 0 ? next() : prev(); }
    touchStart.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Image Slider */}
        <div className="relative aspect-square bg-gray-50 rounded-t-2xl overflow-hidden"
          onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}
        >
          {allImages.length > 0 ? (
            <img
              src={allImages[idx]}
              alt={product.name}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-16 h-16 text-gray-200" />
            </div>
          )}
          {allImages.length > 1 && (
            <>
              <button onClick={prev}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center
                           rounded-full bg-white/80 shadow hover:bg-white transition-colors">
                <ChevronLeft className="w-4 h-4 text-gray-700" />
              </button>
              <button onClick={next}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center
                           rounded-full bg-white/80 shadow hover:bg-white transition-colors">
                <ChevronRight className="w-4 h-4 text-gray-700" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                {allImages.map((_, i) => (
                  <button key={i} onClick={() => setIdx(i)}
                    className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-primary-600 scale-125' : 'bg-gray-300'}`}
                  />
                ))}
              </div>
            </>
          )}
          <button onClick={onClose}
            className="absolute top-3 left-3 w-8 h-8 flex items-center justify-center rounded-full
                       bg-white/80 shadow hover:bg-white transition-colors">
            <XIcon className="w-4 h-4 text-gray-700" />
          </button>
        </div>

        {/* Product Info */}
        <div className="p-5 space-y-4">
          {product.group_name && (
            <span className="text-xs font-semibold text-primary-500 uppercase tracking-wide">
              {product.group_name}
            </span>
          )}
          <h2 className="text-lg font-bold text-gray-900 leading-snug">{product.name}</h2>

          <div className="flex items-center gap-3 flex-wrap">
            <SupplierBadge name={product.supplier_name} />
            <span className="text-xs text-gray-300 font-mono" dir="ltr">{product.sku}</span>
          </div>

          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold text-gray-900">{formatPrice(product.price)}</span>
            {isVatLiable && vatRate > 0 && (
              <span className="text-sm text-gray-400">
                כולל מע״מ: {formatPrice(product.price * (1 + vatRate / 100))}
              </span>
            )}
          </div>

          {isOos && (
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-500">
              <PackageX className="w-4 h-4" />
              אזל מהמלאי
            </div>
          )}

          {/* Description */}
          {product.description && (
            <div className="border-t border-gray-100 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">תיאור מוצר</h3>
              <div
                className="text-sm text-gray-600 leading-relaxed prose prose-sm max-w-none
                           [&_ul]:list-disc [&_ul]:mr-4 [&_ol]:list-decimal [&_ol]:mr-4
                           [&_li]:my-0.5 [&_p]:my-1 [&_strong]:font-semibold
                           [&_a]:text-primary-600 [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.description) }}
              />
            </div>
          )}

          {/* Add to cart */}
          {!isOos && (
            <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
              <QtyStepper value={qty} onChange={onQtyChange} min={1} />
              <AddButton onClick={onAdd} disabled={isOos} viewOnly={viewOnly} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Product image placeholder ────────────────────────────────────────────────

function ProductImage({
  src, alt, className,
}: { src: string | null; alt: string; className?: string }) {
  return src ? (
    <img
      src={src}
      alt={alt}
      className={`object-cover ${className ?? ''}`}
      loading="lazy"
      onError={e => { (e.target as HTMLImageElement).src = ''; }}
    />
  ) : (
    <div className={`flex items-center justify-center bg-gray-100 ${className ?? ''}`}>
      <ShoppingBag className="w-1/3 h-1/3 text-gray-300" />
    </div>
  );
}

// ─── Add-to-cart feedback flash ───────────────────────────────────────────────

interface AddButtonProps { onClick: () => void; disabled?: boolean; viewOnly?: boolean; }
function AddButton({ onClick, disabled, viewOnly }: AddButtonProps) {
  const [flash, setFlash] = useState(false);
  const handle = () => {
    onClick();
    setFlash(true);
    setTimeout(() => setFlash(false), 900);
  };

  if (viewOnly) {
    return (
      <button
        type="button"
        disabled
        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold
                   bg-gray-100 text-gray-400 cursor-not-allowed shrink-0"
      >
        <Eye className="w-3.5 h-3.5" />
        צפייה בלבד
      </button>
    );
  }

  if (disabled) {
    return (
      <button
        type="button"
        disabled
        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold
                   bg-gray-100 text-gray-400 cursor-not-allowed shrink-0"
      >
        <PackageX className="w-3.5 h-3.5" />
        אזל
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handle}
      className={[
        'flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold',
        'transition-all duration-200 active:scale-95 shrink-0',
        flash
          ? 'bg-green-500 text-white'
          : 'bg-primary-600 hover:bg-primary-700 text-white',
      ].join(' ')}
    >
      {flash
        ? <><CheckCircle2 className="w-3.5 h-3.5" />נוסף!</>
        : <><ShoppingCart className="w-3.5 h-3.5" />הוסף</>
      }
    </button>
  );
}

// ─── Grid card ────────────────────────────────────────────────────────────────

function GridCard({
  product, qty, onQtyChange, onAdd, vatRate, isVatLiable, viewOnly, onDetail,
}: {
  product: CatalogProduct;
  qty:     number;
  onQtyChange: (n: number) => void;
  onAdd:   () => void;
  vatRate: number;
  isVatLiable: boolean;
  viewOnly?: boolean;
  onDetail?: () => void;
}) {
  const isOos = product.available_qty <= 0 && !product.effective_backorder_allowed;

  return (
    <div className={`bg-white rounded-2xl shadow-sm border
                    flex flex-col hover:shadow-md transition-shadow cursor-pointer
                    ${isOos ? 'border-gray-100 opacity-70' : 'border-gray-100'}`}
         onClick={onDetail}>
      <div className="aspect-[4/3] w-full relative overflow-hidden rounded-t-2xl">
        <ProductImage src={product.image_url} alt={product.name} className="w-full h-full" />
        {isOos && (
          <div className="absolute inset-0 flex items-center justify-center
                          bg-white/60 backdrop-blur-[1px]">
            <span className="flex items-center gap-1 px-2.5 py-1 bg-gray-700/80 text-white
                             text-[10px] font-bold rounded-full">
              <PackageX className="w-3 h-3" />
              אזל מהמלאי
            </span>
          </div>
        )}
      </div>
      <div className="p-3 flex flex-col gap-2 flex-1">
        {product.group_name && (
          <span className="text-[10px] font-semibold text-primary-500 uppercase tracking-wide">
            {product.group_name}
          </span>
        )}
        <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 flex-1">
          {product.name}
        </p>
        <div className="flex items-center justify-between gap-1 min-w-0">
          <SupplierBadge name={product.supplier_name} />
          <p className="text-[10px] text-gray-300 font-mono shrink-0" dir="ltr">{product.sku}</p>
        </div>
        <p className="text-base font-bold text-gray-900">{formatPrice(product.price)}</p>
        {isVatLiable && vatRate > 0 && (
          <p className="text-[11px] text-gray-400 -mt-1">
            כולל מע״מ: {formatPrice(product.price * (1 + vatRate / 100))}
          </p>
        )}
        <div className="flex items-center justify-between gap-2 mt-auto pt-1" onClick={e => e.stopPropagation()}>
          <QtyStepper value={qty} onChange={onQtyChange} min={1} />
          <AddButton onClick={onAdd} disabled={isOos} viewOnly={viewOnly} />
        </div>
      </div>
    </div>
  );
}

// ─── List row ─────────────────────────────────────────────────────────────────

function ListRow({
  product, qty, onQtyChange, onAdd, vatRate, isVatLiable, viewOnly, onDetail,
}: {
  product: CatalogProduct;
  qty:     number;
  onQtyChange: (n: number) => void;
  onAdd:   () => void;
  vatRate: number;
  isVatLiable: boolean;
  viewOnly?: boolean;
  onDetail?: () => void;
}) {
  const isOos = product.available_qty <= 0 && !product.effective_backorder_allowed;

  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-100
                    flex items-center gap-3 p-3 hover:shadow-md transition-shadow cursor-pointer
                    ${isOos ? 'opacity-70' : ''}`}
         onClick={onDetail}>
      <div className="w-16 h-16 shrink-0 rounded-xl overflow-hidden relative">
        <ProductImage src={product.image_url} alt={product.name} className="w-full h-full" />
        {isOos && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60">
            <PackageX className="w-5 h-5 text-gray-500" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 space-y-0.5">
        {product.group_name && (
          <p className="text-[10px] font-semibold text-primary-500 uppercase tracking-wide">
            {product.group_name}
          </p>
        )}
        <p className="text-sm font-semibold text-gray-900 truncate">{product.name}</p>
        <div className="flex items-center gap-2 min-w-0">
          <SupplierBadge name={product.supplier_name} />
          <p className="text-[10px] text-gray-300 font-mono shrink-0" dir="ltr">{product.sku}</p>
        </div>
        <div className="flex items-center gap-2">
          <p className="text-sm font-bold text-gray-800">{formatPrice(product.price)}</p>
          {isVatLiable && vatRate > 0 && (
            <span className="text-[10px] text-gray-400">כולל מע״מ: {formatPrice(product.price * (1 + vatRate / 100))}</span>
          )}
          {isOos && (
            <span className="text-[10px] text-red-500 font-semibold">אזל מהמלאי</span>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-2 shrink-0" onClick={e => e.stopPropagation()}>
        <QtyStepper value={qty} onChange={onQtyChange} min={1} />
        <AddButton onClick={onAdd} disabled={isOos} viewOnly={viewOnly} />
      </div>
    </div>
  );
}

// ─── Supplier Filter Dropdown ─────────────────────────────────────────────────

interface SupplierOption {
  id:   string;
  name: string;
}

interface SupplierFilterProps {
  suppliers:          SupplierOption[];
  selectedSupplierId: string | null;
  onChange:           (id: string | null) => void;
}

function SupplierFilter({ suppliers, selectedSupplierId, onChange }: SupplierFilterProps) {
  const [open, setOpen] = useState(false);
  const ref             = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectedName = selectedSupplierId
    ? (suppliers.find(s => s.id === selectedSupplierId)?.name ?? 'ספק')
    : 'כל הספקים';

  const isFiltered = !!selectedSupplierId;

  if (suppliers.length === 0) {
    return (
      <div
        className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-medium
                   bg-gray-50 border-gray-200 text-gray-400 whitespace-nowrap shrink-0 select-none"
      >
        <Building2 className="w-4 h-4 shrink-0 text-gray-300" />
        <span className="hidden sm:block">ספק</span>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        onClick={() => setOpen(o => !o)}
        className={[
          'flex items-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-medium',
          'transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300 whitespace-nowrap',
          isFiltered
            ? 'bg-primary-600 border-primary-600 text-white'
            : open
              ? 'bg-primary-50 border-primary-300 text-primary-700'
              : 'bg-white border-gray-200 text-gray-600 hover:border-primary-300 hover:text-primary-700',
        ].join(' ')}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="סינון לפי ספק"
      >
        <Building2 className="w-4 h-4 shrink-0" />
        <span className="hidden sm:block max-w-[90px] truncate">{selectedName}</span>
        {isFiltered ? (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onChange(null); }}
            className="p-0.5 rounded hover:bg-white/20 transition-colors"
            aria-label="נקה סינון ספק"
          >
            <XIcon className="w-3 h-3" />
          </button>
        ) : (
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>

      {open && (
        <div
          className="absolute top-full mt-1 right-0 z-40 min-w-[180px] max-w-xs
                     bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden"
          role="listbox"
        >
          <div className="px-3 pt-2.5 pb-1.5 border-b border-gray-100">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
              סינון לפי ספק
            </p>
          </div>
          <div className="py-1">
            <button
              role="option"
              aria-selected={!selectedSupplierId}
              onClick={() => { onChange(null); setOpen(false); }}
              className={[
                'w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-right transition-colors',
                !selectedSupplierId
                  ? 'bg-primary-50 text-primary-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50',
              ].join(' ')}
            >
              <Store className="w-3.5 h-3.5 shrink-0 text-gray-300" />
              <span className="flex-1 text-right">כל הספקים</span>
              {!selectedSupplierId && <CheckCircle2 className="w-3.5 h-3.5 text-primary-500 shrink-0" />}
            </button>

            {suppliers.map(s => {
              const isActive = s.id === selectedSupplierId;
              return (
                <button
                  key={s.id}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => { onChange(s.id); setOpen(false); }}
                  className={[
                    'w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-right transition-colors',
                    isActive
                      ? 'bg-primary-50 text-primary-700 font-semibold'
                      : 'text-gray-700 hover:bg-gray-50',
                  ].join(' ')}
                >
                  <Building2 className="w-3.5 h-3.5 shrink-0 text-gray-300" />
                  <span className="flex-1 truncate text-right">{s.name}</span>
                  {isActive && <CheckCircle2 className="w-3.5 h-3.5 text-primary-500 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Group Filter Dropdown (Phase 92) ─────────────────────────────────────────

interface GroupOption {
  id:        string;
  name:      string;
  parent_id: string | null;
}

interface GroupFilterProps {
  groups:           GroupOption[];
  selectedGroupIds: Set<string>;
  onChange:         (ids: Set<string>) => void;
}

function GroupFilter({ groups, selectedGroupIds, onChange }: GroupFilterProps) {
  const [open, setOpen] = useState(false);
  const ref             = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (groups.length === 0) return null;

  const count      = selectedGroupIds.size;
  const isFiltered = count > 0;

  const toggleGroup = (id: string) => {
    const next = new Set(selectedGroupIds);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange(next);
  };

  const clearAll = () => onChange(new Set());

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        onClick={() => setOpen(o => !o)}
        className={[
          'flex items-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-medium',
          'transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300 whitespace-nowrap',
          isFiltered
            ? 'bg-primary-600 border-primary-600 text-white'
            : open
              ? 'bg-primary-50 border-primary-300 text-primary-700'
              : 'bg-white border-gray-200 text-gray-600 hover:border-primary-300 hover:text-primary-700',
        ].join(' ')}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="סינון לפי קבוצה"
      >
        <Filter className="w-4 h-4 shrink-0" />
        <span className="hidden sm:block">
          {isFiltered ? `קבוצות (${count})` : 'קבוצה'}
        </span>
        {isFiltered ? (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); clearAll(); }}
            className="p-0.5 rounded hover:bg-white/20 transition-colors"
            aria-label="נקה סינון קבוצות"
          >
            <XIcon className="w-3 h-3" />
          </button>
        ) : (
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>

      {open && (
        <div
          className="absolute top-full mt-1 right-0 z-40 min-w-[200px] max-w-xs
                     bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden"
        >
          <div className="px-3 pt-2.5 pb-1.5 border-b border-gray-100 flex items-center justify-between">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
              סינון לפי קבוצה
            </p>
            {isFiltered && (
              <button
                onClick={clearAll}
                className="text-[10px] text-primary-500 hover:text-primary-700 font-semibold transition-colors"
              >
                נקה הכל
              </button>
            )}
          </div>
          <div className="py-1 max-h-60 overflow-y-auto">
            {groups.map(g => {
              const isSelected = selectedGroupIds.has(g.id);
              return (
                <button
                  key={g.id}
                  onClick={() => toggleGroup(g.id)}
                  className={[
                    'w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-right transition-colors',
                    isSelected
                      ? 'bg-primary-50 text-primary-700 font-semibold'
                      : 'text-gray-700 hover:bg-gray-50',
                  ].join(' ')}
                  style={{ paddingRight: g.parent_id ? '28px' : undefined }}
                >
                  <FolderTree className="w-3.5 h-3.5 shrink-0 text-amber-400" />
                  <span className="flex-1 truncate text-right">{g.name}</span>
                  {isSelected && (
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary-500 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

type ViewMode = 'grid' | 'list';

export function B2BCatalogPage() {
  const { user, loading: authLoading } = useAuth();
  const { session } = useB2BUser();
  const { addItem } = useB2BCart();

  const ctxCompanyIds: string[] = useMemo(
    () => session?.allowedCompanyIds ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session?.allowedCompanyIds?.join(',')],
  );

  // ── Local state ───────────────────────────────────────────────────────────
  const [products,            setProducts]            = useState<CatalogProduct[]>([]);
  const [loading,             setLoading]             = useState(true);
  const [error,               setError]               = useState<string | null>(null);
  const [search,              setSearch]              = useState('');
  const [view,                setView]                = useState<ViewMode>('grid');
  const [selectedSupplierId,  setSelectedSupplierId]  = useState<string | null>(null);
  const [suppliers,           setSuppliers]           = useState<SupplierOption[]>([]);

  // Phase 92: group filter state
  const [filterGroups,     setFilterGroups]     = useState<GroupOption[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());

  const fallbackAttempted = useRef(false);
  const prevCtxKey        = useRef('');

  const [pendingQty, setPendingQty] = useState<Record<string, number>>({});
  const getPending = (id: string) => pendingQty[id] ?? 1;
  const setPending = (id: string, n: number) =>
    setPendingQty(prev => ({ ...prev, [id]: Math.max(1, n) }));

  // ── VAT rate per supplier company ─────────────────────────────────────────
  const [vatRateByCompany, setVatRateByCompany] = useState<Record<string, number>>({});

  const isVatLiable = session?.activeClientRecord?.is_vat_liable ?? true;

  useEffect(() => {
    if (ctxCompanyIds.length === 0) return;

    supabase
      .rpc('get_company_vat_rates', { p_company_ids: ctxCompanyIds })
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, number> = {};
        for (const r of data as { company_id: string; vat_rate: number | null }[]) {
          if (r.vat_rate != null) map[r.company_id] = Number(r.vat_rate);
        }
        setVatRateByCompany(map);
      });
  }, [ctxCompanyIds]);

  // ── Fetch supplier names ──────────────────────────────────────────────────
  useEffect(() => {
    if (ctxCompanyIds.length === 0) return;
    supabase
      .from('companies')
      .select('id, legal_name')
      .in('id', ctxCompanyIds)
      .order('legal_name')
      .then(({ data }) => {
        if (!data) return;
        setSuppliers(
          (data as { id: string; legal_name: string }[]).map(r => ({
            id:   r.id,
            name: r.legal_name ?? r.id,
          })),
        );
      });
  }, [ctxCompanyIds.join(',')]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reset supplier + group filters when business group changes ────────────
  // When there is exactly one supplier, auto-select it so group filters load
  // immediately without requiring the user to open the supplier dropdown.
  useEffect(() => {
    if (ctxCompanyIds.length === 1) {
      setSelectedSupplierId(ctxCompanyIds[0] ?? null);
    } else {
      setSelectedSupplierId(null);
    }
    setSelectedGroupIds(new Set());
    setFilterGroups([]);
  }, [ctxCompanyIds.join(',')]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Phase 92: load filter groups when supplier is selected ────────────────
  useEffect(() => {
    if (!selectedSupplierId) {
      setFilterGroups([]);
      setSelectedGroupIds(new Set());
      return;
    }
    supabase
      .rpc('get_b2b_supplier_filter_groups', { p_company_id: selectedSupplierId })
      .then(({ data }) => {
        setFilterGroups(
          (data ?? []).map((r: { id: string; name: string; parent_id: string | null }) => ({
            id:        r.id,
            name:      r.name,
            parent_id: r.parent_id ?? null,
          })),
        );
        setSelectedGroupIds(new Set());
      });
  }, [selectedSupplierId]);

  // ── Fallback: re-resolve company IDs from inventory_clients ──────────────
  const resolveCompanyIds = useCallback(async (): Promise<string[]> => {
    if (!user) return [];
    const orParts = [
      user.email ? `email.eq.${user.email}` : null,
      user.phone ? `phone.eq.${user.phone}` : null,
    ].filter(Boolean).join(',');
    if (!orParts) return [];
    const { data, error: dbErr } = await supabase
      .from('inventory_clients')
      .select('company_id')
      .eq('b2b_allowed', true)
      .or(orParts);
    if (dbErr || !data || data.length === 0) return [];
    return [...new Set((data as { company_id: string }[]).map(r => r.company_id))];
  }, [user]);

  // Stable serialized key for selectedGroupIds to use in dep arrays
  const selectedGroupIdsKey = useMemo(
    () => Array.from(selectedGroupIds).sort().join(','),
    [selectedGroupIds],
  );

  // ── Orchestrate: resolve IDs → fetch products ────────────────────────────
  useEffect(() => {
    // Wait while Supabase auth is still resolving
    if (authLoading) return;

    // No auth session after auth resolved — stop spinner; let B2BLayout
    // redirect to login if needed. (Normal path: user is always set because
    // whatsapp-otp now creates an auth user and returns a token_hash.)
    if (!user) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function run() {
      const ctxKey = ctxCompanyIds.join(',');

      if (ctxKey !== prevCtxKey.current) {
        prevCtxKey.current        = ctxKey;
        fallbackAttempted.current = false;
      }

      let ids = ctxCompanyIds;
      if (ids.length === 0 && !fallbackAttempted.current) {
        fallbackAttempted.current = true;
        ids = await resolveCompanyIds();
      }

      if (cancelled) return;

      // ── Fetch products ───────────────────────────────────────────────────
      setLoading(true);
      setError(null);

      try {
        const targetIds = selectedSupplierId
          ? ids.filter(id => id === selectedSupplierId)
          : ids;

        if (targetIds.length === 0) {
          if (!cancelled) { setProducts([]); setLoading(false); }
          return;
        }

        const groupParam = selectedGroupIds.size > 0
          ? Array.from(selectedGroupIds)
          : null;

        const { data, error: dbErr } = await supabase
          .rpc('get_b2b_catalog', {
            p_company_ids: targetIds,
            ...(groupParam ? { p_group_ids: groupParam } : {}),
          }).limit(10000);

        if (cancelled) return;
        if (dbErr) throw dbErr;

        // ── Apply client-specific pricelist pricing ────────────────────────
        const priceOverrideMap = new Map<string, number>();
        const pricelistByCompany = session?.pricelistByCompany ?? {};
        const companiesWithPricelist = targetIds
          .map(cid => ({ companyId: cid, pricelistId: pricelistByCompany[cid] }))
          .filter((x): x is { companyId: string; pricelistId: string } => !!x.pricelistId);

        if (companiesWithPricelist.length > 0) {
          const byPricelist = new Map<string, string[]>();
          for (const { companyId, pricelistId } of companiesWithPricelist) {
            const arr = byPricelist.get(pricelistId) ?? [];
            arr.push(companyId);
            byPricelist.set(pricelistId, arr);
          }

          const priceResults = await Promise.all(
            Array.from(byPricelist.entries()).map(([pricelistId, cids]) =>
              supabase.rpc('get_b2b_pricelist_prices', {
                p_company_ids:  cids,
                p_pricelist_id: pricelistId,
              }),
            ),
          );

          if (!cancelled) {
            for (const result of priceResults) {
              if (result.data) {
                for (const row of result.data as any[]) {
                  if (row.price_after_discount != null) {
                    priceOverrideMap.set(row.product_id, Number(row.price_after_discount));
                  }
                }
              }
            }
          }
        }

        if (!cancelled) {
          setProducts(
            (data ?? []).map((r: any) => ({
              id:                          r.id,
              company_id:                  r.company_id,
              sku:                         r.sku,
              name:                        r.name,
              price:                       priceOverrideMap.get(r.id) ?? r.price,
              image_url:                   r.image_url ?? null,
              group_name:                  r.group_name ?? null,
              supplier_name:               r.supplier_name ?? null,
              available_qty:               Number(r.available_qty ?? 0),
              effective_backorder_allowed: Boolean(r.effective_backorder_allowed),
              description:                 r.description ?? null,
            })),
          );
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'שגיאה בטעינת המוצרים');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, ctxCompanyIds.join(','), selectedSupplierId, selectedGroupIdsKey, resolveCompanyIds]);

  // ── Filtered list (search) ────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p =>
      p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q),
    );
  }, [products, search]);

  // ── Add to cart ───────────────────────────────────────────────────────────
  const isViewOnly = session?.role === 'client'
    && session.activeClientRecord?.client_access === 'view_only';

  const handleAdd = (product: CatalogProduct) => {
    if (isViewOnly) return;
    addItem(product, getPending(product.id));
    setPending(product.id, 1);
  };

  // ── Product detail modal ──────────────────────────────────────────────────
  const [detailProduct, setDetailProduct] = useState<CatalogProduct | null>(null);
  const [detailImages, setDetailImages] = useState<{ image_url: string; alt_text: string | null; position: number }[]>([]);

  const openDetail = useCallback(async (product: CatalogProduct) => {
    setDetailProduct(product);
    setDetailImages([]);
    const { data } = await supabase
      .from('inventory_product_images')
      .select('image_url, alt_text, position')
      .eq('product_id', product.id)
      .order('position');
    if (data && data.length > 0) setDetailImages(data);
  }, []);

  // ── Supplier change: also clear group filter ──────────────────────────────
  const handleSupplierChange = (id: string | null) => {
    setSelectedSupplierId(id);
    // group state is reset inside the useEffect that watches selectedSupplierId
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto px-4 py-5 space-y-4">

      {isViewOnly && (
        <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          <Eye className="w-4 h-4 shrink-0" />
          <span>מצב צפייה בלבד — לא ניתן להוסיף מוצרים לסל או לבצע הזמנה</span>
        </div>
      )}

      {/* ── Toolbar ── */}
      <div className="flex items-center gap-2 flex-wrap">

        {/* Search */}
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400
                             pointer-events-none" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="חפש לפי שם או מק״ט…"
            className="w-full pr-9 pl-3 py-2.5 rounded-xl border border-gray-200 bg-white
                       text-sm focus:outline-none focus:ring-2 focus:ring-primary-300
                       focus:border-transparent placeholder:text-gray-400"
          />
        </div>

        {/* Supplier Filter — hidden when there is only one supplier */}
        {ctxCompanyIds.length > 1 && (
          <SupplierFilter
            suppliers={suppliers}
            selectedSupplierId={selectedSupplierId}
            onChange={handleSupplierChange}
          />
        )}

        {/* Group Filter — shown when supplier has filter groups marked use_as_b2b_filter */}
        {selectedSupplierId && filterGroups.length > 0 && (
          <GroupFilter
            groups={filterGroups}
            selectedGroupIds={selectedGroupIds}
            onChange={setSelectedGroupIds}
          />
        )}

        {/* Grid / list toggle */}
        <div className="flex items-center gap-1 p-1 bg-white border border-gray-200 rounded-xl shrink-0">
          <button
            onClick={() => setView('grid')}
            className={[
              'p-2 rounded-lg transition-colors',
              view === 'grid' ? 'bg-primary-600 text-white' : 'text-gray-400 hover:text-gray-600',
            ].join(' ')}
            aria-label="תצוגת גריד"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setView('list')}
            className={[
              'p-2 rounded-lg transition-colors',
              view === 'list' ? 'bg-primary-600 text-white' : 'text-gray-400 hover:text-gray-600',
            ].join(' ')}
            aria-label="תצוגת רשימה"
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Active filter chips */}
      {(session?.activeClientRecord || selectedSupplierId || selectedGroupIds.size > 0) && (
        <div className="flex items-center gap-2 flex-wrap">
          {session?.activeClientRecord && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full
                             bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-100">
              <Building2 className="w-3 h-3 text-blue-500" />
              מזמין עבור: {session.activeClientRecord.name}
              {session.activeClientRecord.business_id && (
                <span className="font-mono font-normal text-blue-500">
                  · ח.פ. {session.activeClientRecord.business_id}
                </span>
              )}
            </span>
          )}

          {selectedSupplierId && suppliers.find(s => s.id === selectedSupplierId) && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full
                             bg-primary-100 text-primary-700 text-xs font-semibold">
              <Building2 className="w-3 h-3" />
              {suppliers.find(s => s.id === selectedSupplierId)!.name}
              <button
                onClick={() => handleSupplierChange(null)}
                className="mr-0.5 opacity-60 hover:opacity-100 transition-opacity"
                aria-label="הסר סינון ספק"
              >
                <XIcon className="w-3 h-3" />
              </button>
            </span>
          )}

          {selectedGroupIds.size > 0 && (
            <>
              {Array.from(selectedGroupIds).map(gid => {
                const g = filterGroups.find(x => x.id === gid);
                if (!g) return null;
                return (
                  <span
                    key={gid}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full
                               bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-100"
                  >
                    <Filter className="w-3 h-3 text-amber-500" />
                    {g.name}
                    <button
                      onClick={() => {
                        const next = new Set(selectedGroupIds);
                        next.delete(gid);
                        setSelectedGroupIds(next);
                      }}
                      className="mr-0.5 opacity-60 hover:opacity-100 transition-opacity"
                      aria-label={`הסר סינון ${g.name}`}
                    >
                      <XIcon className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* Results count */}
      {!loading && !error && (
        <p className="text-xs text-gray-400">
          {filtered.length === products.length
            ? `${products.length} מוצרים`
            : `${filtered.length} תוצאות מתוך ${products.length} מוצרים`
          }
        </p>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-sm">טוען מוצרים…</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200
                        rounded-2xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">שגיאה בטעינת הקטלוג</p>
            <p className="text-xs mt-0.5 text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
          <ShoppingBag className="w-14 h-14 text-gray-200" />
          <p className="text-sm font-medium">
            {products.length === 0 ? 'אין מוצרים זמינים' : 'לא נמצאו תוצאות'}
          </p>
          {search && (
            <button onClick={() => setSearch('')}
                    className="text-xs text-primary-600 hover:underline">
              נקה חיפוש
            </button>
          )}
          {selectedSupplierId && (
            <button onClick={() => handleSupplierChange(null)}
                    className="text-xs text-primary-600 hover:underline">
              הצג את כל הספקים
            </button>
          )}
          {selectedGroupIds.size > 0 && (
            <button onClick={() => setSelectedGroupIds(new Set())}
                    className="text-xs text-primary-600 hover:underline">
              נקה סינון קבוצות
            </button>
          )}
        </div>
      )}

      {/* Grid view */}
      {!loading && !error && view === 'grid' && filtered.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map(p => (
            <GridCard
              key={p.id}
              product={p}
              qty={getPending(p.id)}
              onQtyChange={n => setPending(p.id, n)}
              onAdd={() => handleAdd(p)}
              vatRate={vatRateByCompany[p.company_id] ?? 0}
              isVatLiable={isVatLiable}
              viewOnly={isViewOnly}
              onDetail={() => openDetail(p)}
            />
          ))}
        </div>
      )}

      {/* List view */}
      {!loading && !error && view === 'list' && filtered.length > 0 && (
        <div className="space-y-2">
          {filtered.map(p => (
            <ListRow
              key={p.id}
              product={p}
              qty={getPending(p.id)}
              onQtyChange={n => setPending(p.id, n)}
              onAdd={() => handleAdd(p)}
              vatRate={vatRateByCompany[p.company_id] ?? 0}
              isVatLiable={isVatLiable}
              viewOnly={isViewOnly}
              onDetail={() => openDetail(p)}
            />
          ))}
        </div>
      )}

      {/* Product Detail Modal */}
      {detailProduct && (
        <ProductDetailModal
          product={detailProduct}
          images={detailImages}
          onClose={() => setDetailProduct(null)}
          qty={getPending(detailProduct.id)}
          onQtyChange={n => setPending(detailProduct.id, n)}
          onAdd={() => handleAdd(detailProduct)}
          vatRate={vatRateByCompany[detailProduct.company_id] ?? 0}
          isVatLiable={isVatLiable}
          viewOnly={isViewOnly}
        />
      )}
    </div>
  );
}
